import {
  InvoiceProjectByStatus,
  projectStatusByCode,
  invoiceControlStatusConfig,
  Team,
  User,
} from 'entities';

import { Row, Dot } from 'components/Blocks';
import { ListFilters } from 'components/ListManipulation';
import { getUserLabel } from 'components/Inputs/SelectLabels';

import { calculateAmountTaken } from 'pages/Projects/ProjectProfile/Invoice/ReadyToInvoice/utils';

export function getProjectFilters(
  status: string,
  teamData: Team[],
): ListFilters<InvoiceProjectByStatus>[] {
  const { unassigned, waitingInvoiceInfo } = invoiceControlStatusConfig;

  const filters: (ListFilters<InvoiceProjectByStatus> | null)[] = [
    status !== unassigned.code
      ? {
          getLabel: (t) => t('invoice-person'),

          property: 'owner',

          multiple: true,

          getOptionNode: (user) => getUserLabel(user as unknown as User),

          optionExtractor: ({ owner }) => ({
            ...owner,
            value: owner.id,
            name: owner.name,
          }),
        }
      : null,

    {
      getLabel: (t) => t('client'),

      property: 'client',

      multiple: true,

      optionExtractor: ({ project, client }) => ({
        value: client,
        name: project.clientName,
      }),
    },

    {
      getLabel: (t) => t('team-responsible'),

      property: 'project',

      multiple: true,

      optionExtractor: ({ project: { owner } }) => ({
        value: owner.team,
        name: teamData.find(({ id }) => id === owner.team)?.name || 'NOT ASSIGNED',
      }),
    },

    {
      getLabel: (t) => t('owner'),

      property: 'owner',

      multiple: true,

      getOptionNode: (user) => getUserLabel(user as unknown as User),

      optionExtractor: ({ project }) => ({
        ...project.owner,
        value: project.owner.id,
        name: project.owner.name,
      }),
    },

    {
      getLabel: (t) => t('status'),

      property: 'status',

      multiple: true,

      optionExtractor: ({ project: { status } }, t) => ({
        value: status,
        name: projectStatusByCode[status].getLabel(t),
      }),

      getOptionNode: ({ name, value }) => (
        <Row gap="1.5">
          <Dot color={projectStatusByCode[value].color} />

          <span>{name}</span>
        </Row>
      ),
    },

    status !== unassigned.code
      ? {
          getLabel: (t) => t('payment-method'),

          property: 'type',

          multiple: true,

          optionExtractor: ({ type }, t) => ({
            value: `${type}`,
            name: type ?? t('no-payment-method'),
          }),
        }
      : null,

    status === waitingInvoiceInfo.code
      ? {
          getLabel: (t) => t('ready-to-invoice'),

          property: 'readyToInvoice',

          multiple: false,

          optionExtractor: ({ readyToInvoice }, t) => ({
            value: `${readyToInvoice}`,
            name: t(readyToInvoice ? 'yes' : 'no'),
          }),
        }
      : null,

    [unassigned.code, waitingInvoiceInfo.code].includes(status)
      ? null
      : {
          getLabel: (t) => t('amount-available'),

          property: 'outsourceInvoices',

          multiple: false,

          optionExtractor: (control, t) => {
            const isAvailable = calculateAmountTaken(control) !== Number(control.project.total);

            return {
              value: `${calculateAmountTaken(control) !== Number(control.project.total)}`,
              name: t(isAvailable ? 'yes' : 'no'),
            };
          },
        },
  ];

  return filters.filter(Boolean) as ListFilters<InvoiceProjectByStatus>[];
}
